import { Reducer } from 'redux'

export interface WAFRQuestionAnswerResponse {
  workload_id: string
  lens_alias: string
  lens_arn: string
  answer: Answer
  progressBarPercentage: number
}

export interface Comment {
  id: number
  userId: string
  createdAt: string
  message: string
  firstname: string
  lastname: string
}

export interface Answer {
  questionId: string
  pillarId: string
  questionTitle: string
  questionDescription: string
  improvementPlanUrl: string
  helpfulResourceUrl: string
  choices: Choice[]
  selectedChoices: string[]
  choiceAnswers: ChoiceAnswer[]
  isApplicable: boolean
  risk: RiskLevel
  reason: string
  notes: string
  choiceUpdates?: ChoiceUpdates
  isFlagged?: boolean
}

export interface Choice {
  choiceId: string
  title: string
  description: string
  risk: string
}

export interface ChoiceAnswer {
  choiceId: string
  status: ChoiceStatus
  reason?: string
  notes?: string
}

export interface ChoiceUpdate {
  status: 'SELECTED' | 'NOT_APPLICABLE' | 'UNSELECTED' | 'NOT_SELECTED'
  reason:
    | 'OUT_OF_SCOPE'
    | 'BUSINESS_PRIORITIES'
    | 'ARCHITECTURE_CONSTRAINTS'
    | 'OTHER'
    | 'NONE'
  notes?: string
}

interface ChoiceUpdates {
  [key: string]: ChoiceUpdate
}

export type ChoiceStatus =
  | 'SELECTED'
  | 'NOT_SELECTED'
  | 'NOT_APPLICABLE'
  | 'UNSELECTED'
export type RiskLevel = 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH'

export enum WAFRQuestionAnswerDetailAction {
  SET_WELL_ARCHITECTED_RESPONSE = 'SET_WELL_ARCHITECTED_RESPONSE',
  UPDATE_RISK_LEVEL = 'UPDATE_RISK_LEVEL',
  TOGGLE_APPLICABILITY = 'TOGGLE_APPLICABILITY',
  UPDATE_CHOICE_ANSWER = 'UPDATE_CHOICE_ANSWER',
  SET_ERROR = 'SET_ERROR',
  UPDATE_IS_APPLICABLE = 'UPDATE_IS_APPLICABLE',
  UPDATE_REASON = 'UPDATE_REASON',
  UPDATE_CHOICE_UPDATES = 'UPDATE_CHOICE_UPDATES',
  SET_COMMENTS = 'SET_COMMENTS',
  CLEAR_COMMENTS = 'CLEAR_COMMENTS',
  APPEND_COMMENTS = 'APPEND_COMMENTS',
  SET_TOTAL_COMMENTS = 'SET_TOTAL_COMMENTS',
  SET_NEXT_TOKEN = 'SET_NEXT_TOKEN',
  SET_HAS_MORE = 'SET_HAS_MORE',
}

export interface WAFRQuestionAnswerState {
  wafrQuestionAnswerResponse: WAFRQuestionAnswerResponse | null
  risk: RiskLevel
  isApplicable: boolean
  error: string
  comments: Comment[]
  totalComments: number
  nextToken?: string | null
  hasMore?: boolean
}

const initialState: WAFRQuestionAnswerState = {
  wafrQuestionAnswerResponse: null,
  risk: 'NONE',
  isApplicable: false,
  error: '',
  comments: [],
  totalComments: 0,
  nextToken: null,
  hasMore: false,
}

export type actionType =
  | {
      type: WAFRQuestionAnswerDetailAction.SET_WELL_ARCHITECTED_RESPONSE
      payload: WAFRQuestionAnswerResponse
    }
  | {
      type: WAFRQuestionAnswerDetailAction.UPDATE_RISK_LEVEL
      payload: RiskLevel
    }
  | {
      type: WAFRQuestionAnswerDetailAction.TOGGLE_APPLICABILITY
    }
  | {
      type: WAFRQuestionAnswerDetailAction.SET_ERROR
      payload: string
    }
  | {
      type: WAFRQuestionAnswerDetailAction.UPDATE_CHOICE_ANSWER
      payload: {
        choiceId: string
        updates: {
          status?: ChoiceStatus
          notes?: string
        }
      }
    }
  | {
      type: WAFRQuestionAnswerDetailAction.UPDATE_IS_APPLICABLE
      payload: boolean
    }
  | {
      type: WAFRQuestionAnswerDetailAction.UPDATE_REASON
      payload: string
    }
  | {
      type: WAFRQuestionAnswerDetailAction.UPDATE_CHOICE_UPDATES
      payload: {
        choiceId: string
        updates: ChoiceUpdate
      }
    }
  | {
      type: WAFRQuestionAnswerDetailAction.SET_COMMENTS
      payload: Comment[]
    }
  | {
      type: WAFRQuestionAnswerDetailAction.CLEAR_COMMENTS
    }
  | {
      type: WAFRQuestionAnswerDetailAction.APPEND_COMMENTS
      payload: Comment[]
    }
  | {
      type: WAFRQuestionAnswerDetailAction.SET_TOTAL_COMMENTS
      payload: number
    }
  | {
      type: WAFRQuestionAnswerDetailAction.SET_NEXT_TOKEN
      payload: string | null
    }
  | {
      type: WAFRQuestionAnswerDetailAction.SET_HAS_MORE
      payload: boolean
    }

export const reducer: Reducer<WAFRQuestionAnswerState, actionType> = (
  state = initialState,
  action: actionType
): WAFRQuestionAnswerState => {
  switch (action.type) {
    case WAFRQuestionAnswerDetailAction.SET_WELL_ARCHITECTED_RESPONSE:
      return {
        ...state,
        wafrQuestionAnswerResponse: action.payload,
      }

    case WAFRQuestionAnswerDetailAction.UPDATE_RISK_LEVEL:
      return {
        ...state,
        risk: action.payload,
      }

    case WAFRQuestionAnswerDetailAction.TOGGLE_APPLICABILITY:
      return {
        ...state,
        isApplicable: !state.isApplicable,
      }

    case WAFRQuestionAnswerDetailAction.SET_ERROR:
      return {
        ...state,
        error: action.payload,
      }

    case WAFRQuestionAnswerDetailAction.UPDATE_IS_APPLICABLE:
      return {
        ...state,
        wafrQuestionAnswerResponse: state.wafrQuestionAnswerResponse
          ? {
              ...state.wafrQuestionAnswerResponse,
              answer: {
                ...state.wafrQuestionAnswerResponse.answer,
                isApplicable: action.payload,
              },
            }
          : state.wafrQuestionAnswerResponse,
      }
    case WAFRQuestionAnswerDetailAction.UPDATE_REASON:
      return {
        ...state,
        wafrQuestionAnswerResponse: state.wafrQuestionAnswerResponse
          ? {
              ...state.wafrQuestionAnswerResponse,
              answer: {
                ...state.wafrQuestionAnswerResponse.answer,
                reason: action.payload,
              },
            }
          : state.wafrQuestionAnswerResponse,
      }

    case WAFRQuestionAnswerDetailAction.UPDATE_CHOICE_ANSWER: {
      const { choiceId, updates } = action.payload

      if (!state.wafrQuestionAnswerResponse) return state

      const existingAnswers =
        state.wafrQuestionAnswerResponse.answer.choiceAnswers
      const answerIndex = existingAnswers.findIndex(
        choice => choice?.choiceId === choiceId
      )

      let newChoiceAnswers

      if (answerIndex !== -1) {
        newChoiceAnswers = existingAnswers.map(choice =>
          choice?.choiceId === choiceId
            ? { ...choice, status: updates.status ?? choice.status }
            : choice
        )
      } else {
        const newAnswer = {
          choiceId,
          status: updates.status ?? 'SELECTED',
        }
        newChoiceAnswers = [...existingAnswers, newAnswer]
      }

      const currentSelectedChoices =
        state.wafrQuestionAnswerResponse.answer.selectedChoices || []

      let newSelectedChoices: string[]

      if (choiceId === '') {
        // If choiceId is empty string, clear all selected choices
        newSelectedChoices = []
      } else if (updates.status === 'SELECTED') {
        // Add to selectedChoices only if not already present
        newSelectedChoices = currentSelectedChoices.includes(choiceId)
          ? currentSelectedChoices
          : [...currentSelectedChoices, choiceId]
      } else if (updates.status === 'NOT_SELECTED') {
        // Remove from selectedChoices if present
        newSelectedChoices = currentSelectedChoices.filter(
          id => id !== choiceId
        )
      } else {
        newSelectedChoices = currentSelectedChoices
      }

      return {
        ...state,
        wafrQuestionAnswerResponse: {
          ...state.wafrQuestionAnswerResponse,
          answer: {
            ...state.wafrQuestionAnswerResponse.answer,
            choiceAnswers: newChoiceAnswers,
            selectedChoices: newSelectedChoices,
            notes:
              updates.notes ?? state.wafrQuestionAnswerResponse.answer.notes,
          },
        },
      }
    }
    case WAFRQuestionAnswerDetailAction.SET_COMMENTS: {
      return {
        ...state,
        comments: action.payload,
      }
    }
    case WAFRQuestionAnswerDetailAction.CLEAR_COMMENTS: {
      return {
        ...state,
        comments: [],
      }
    }
    case WAFRQuestionAnswerDetailAction.SET_TOTAL_COMMENTS: {
      return {
        ...state,
        totalComments: action.payload,
      }
    }
    case WAFRQuestionAnswerDetailAction.UPDATE_CHOICE_UPDATES: {
      const { choiceId, updates } = action.payload

      if (!state.wafrQuestionAnswerResponse) return state

      const existingChoiceUpdates =
        state.wafrQuestionAnswerResponse.answer.choiceUpdates || {}

      const updatedChoiceUpdates = {
        ...existingChoiceUpdates,
        [choiceId]: {
          ...existingChoiceUpdates[choiceId],
          ...updates,
        },
      }

      return {
        ...state,
        wafrQuestionAnswerResponse: {
          ...state.wafrQuestionAnswerResponse,
          answer: {
            ...state.wafrQuestionAnswerResponse.answer,
            choiceUpdates: updatedChoiceUpdates,
          },
        },
      }
    }
    case WAFRQuestionAnswerDetailAction.APPEND_COMMENTS: {
      return {
        ...state,
        comments: [...state.comments, ...action.payload],
      }
    }
    case WAFRQuestionAnswerDetailAction.SET_NEXT_TOKEN: {
      return {
        ...state,
        nextToken: action.payload,
      }
    }
    case WAFRQuestionAnswerDetailAction.SET_HAS_MORE: {
      return {
        ...state,
        hasMore: action.payload,
      }
    }
    default:
      return state
  }
}
